/**
 * Strict type definitions for CRM Timeline feature.
 *
 * Uses discriminated unions for type-safe event payloads.
 * Zero `any` types - full type safety from API to UI.
 */

import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { EntityTypeName, PropertyValue } from '@/types/crm';

import {
  hasProperty,
  isObject,
  isString,
  optional,
  validateShape,
} from '../../../utils/typeGuards';

// ============================================================================
// Event Type Constants
// ============================================================================

export const EVENT_TYPES = {
  // Lifecycle
  OBJECT_CREATED: 'ahoy_object_created',
  OBJECT_DELETED: 'ahoy_object_deleted',
  OBJECT_MERGED: 'ahoy_object_merged',
  OBJECT_RESTORED: 'ahoy_object_restored',

  // Properties
  PROPERTY_UPDATED: 'ahoy_property_updated',

  // Associations
  ASSOCIATION_ADDED: 'ahoy_association_added',
  ASSOCIATION_REMOVED: 'ahoy_association_removed',

  // Emails
  EMAIL_SENT: 'ahoy_email_sent',
  EMAIL_RECEIVED: 'ahoy_email_received',
  EMAIL_OPENED: 'ahoy_email_opened',

  // LinkedIn Messages
  LINKEDIN_MESSAGE_SENT: 'ahoy_linkedin_message_sent',
  LINKEDIN_MESSAGE_RECEIVED: 'ahoy_linkedin_message_received',

  // Calls & SMS
  AHOY_CALL_COMPLETED: 'ahoy_call_completed',
  AHOY_CALL_MISSED: 'ahoy_call_missed',
  AHOY_VOICEMAIL_RECEIVED: 'ahoy_voicemail_received',
  AHOY_SMS_SENT: 'ahoy_sms_sent',
  AHOY_SMS_RECEIVED: 'ahoy_sms_received',

  // Website
  WEB_FORM_SUBMITTED: 'ahoy_web_form_submitted',
  WEB_PAGE_VIEWED: 'ahoy_web_page_viewed',
  WEBHOOK_RECEIVED: 'ahoy_webhook_received',

  // Payments
  INVOICE_PAID: 'ahoy_invoice_paid',
  INVOICE_PAYMENT_FAILED: 'ahoy_invoice_payment_failed',
  INVOICE_REFUNDED: 'ahoy_invoice_refunded',

  // System
  ERRORED: 'ahoy_errored',
  /** DEPRECATED write-side — superseded by AUTOMATION_RAN; legacy rows still render. */
  EVENT_PROCESSED: 'ahoy_event_processed',
  AUTOMATION_RAN: 'ahoy_automation_ran',
  AGENT_RAN: 'ahoy_agent_ran',

  // Outbound Webhooks
  AHOY_WEBHOOK_EMITTED: 'ahoy_webhook_emitted',
  AHOY_WEBHOOK_DELIVERED: 'ahoy_webhook_delivered',
  AHOY_WEBHOOK_FAILED: 'ahoy_webhook_failed',
  AHOY_WEBHOOK_REPLAY_REQUESTED: 'ahoy_webhook_replay_requested',
  AHOY_WEBHOOK_ATTEMPT_FAILED: 'ahoy_webhook_attempt_failed',

  // Recall.ai Bot Lifecycle
  AHOY_RECALL_BOT_COMPLETED: 'ahoy_recall_bot_completed',
  AHOY_RECALL_BOT_FAILED: 'ahoy_recall_bot_failed',
} as const;

export type EventTypeName = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];

export const EVENT_TYPE_CATEGORIES = {
  lifecycle: [
    EVENT_TYPES.OBJECT_CREATED,
    EVENT_TYPES.OBJECT_DELETED,
    EVENT_TYPES.OBJECT_MERGED,
    EVENT_TYPES.OBJECT_RESTORED,
  ],
  properties: [EVENT_TYPES.PROPERTY_UPDATED],
  associations: [
    EVENT_TYPES.ASSOCIATION_ADDED,
    EVENT_TYPES.ASSOCIATION_REMOVED,
  ],
  emails: [
    EVENT_TYPES.EMAIL_SENT,
    EVENT_TYPES.EMAIL_RECEIVED,
    EVENT_TYPES.EMAIL_OPENED,
  ],
  linkedInMessages: [
    EVENT_TYPES.LINKEDIN_MESSAGE_SENT,
    EVENT_TYPES.LINKEDIN_MESSAGE_RECEIVED,
  ],
  calls: [
    EVENT_TYPES.AHOY_CALL_COMPLETED,
    EVENT_TYPES.AHOY_CALL_MISSED,
    EVENT_TYPES.AHOY_VOICEMAIL_RECEIVED,
  ],
  sms: [EVENT_TYPES.AHOY_SMS_SENT, EVENT_TYPES.AHOY_SMS_RECEIVED],
  website: [
    EVENT_TYPES.WEB_FORM_SUBMITTED,
    EVENT_TYPES.WEB_PAGE_VIEWED,
    EVENT_TYPES.WEBHOOK_RECEIVED,
  ],
  payments: [
    EVENT_TYPES.INVOICE_PAID,
    EVENT_TYPES.INVOICE_PAYMENT_FAILED,
    EVENT_TYPES.INVOICE_REFUNDED,
  ],
  system: [
    EVENT_TYPES.ERRORED,
    EVENT_TYPES.EVENT_PROCESSED,
    EVENT_TYPES.AUTOMATION_RAN,
    EVENT_TYPES.AGENT_RAN,
  ],
} as const;

export const EVENT_TYPE_LABELS: Record<EventTypeName, string> = {
  [EVENT_TYPES.OBJECT_CREATED]: 'Object Created',
  [EVENT_TYPES.OBJECT_DELETED]: 'Object Deleted',
  [EVENT_TYPES.OBJECT_MERGED]: 'Object Merged',
  [EVENT_TYPES.OBJECT_RESTORED]: 'Object Restored',
  [EVENT_TYPES.PROPERTY_UPDATED]: 'Property Updated',
  [EVENT_TYPES.ASSOCIATION_ADDED]: 'Association Added',
  [EVENT_TYPES.ASSOCIATION_REMOVED]: 'Association Removed',
  [EVENT_TYPES.EMAIL_SENT]: 'Email Sent',
  [EVENT_TYPES.EMAIL_RECEIVED]: 'Email Received',
  [EVENT_TYPES.EMAIL_OPENED]: 'Email Opened',
  [EVENT_TYPES.LINKEDIN_MESSAGE_SENT]: 'LinkedIn Message Sent',
  [EVENT_TYPES.LINKEDIN_MESSAGE_RECEIVED]: 'LinkedIn Message Received',
  [EVENT_TYPES.AHOY_CALL_COMPLETED]: 'Call completed',
  [EVENT_TYPES.AHOY_CALL_MISSED]: 'Missed call',
  [EVENT_TYPES.AHOY_VOICEMAIL_RECEIVED]: 'Voicemail received',
  [EVENT_TYPES.AHOY_SMS_SENT]: 'SMS sent',
  [EVENT_TYPES.AHOY_SMS_RECEIVED]: 'SMS received',
  [EVENT_TYPES.WEB_FORM_SUBMITTED]: 'Web Form Submitted',
  [EVENT_TYPES.WEB_PAGE_VIEWED]: 'Web Page Viewed',
  [EVENT_TYPES.WEBHOOK_RECEIVED]: 'Webhook Received',
  [EVENT_TYPES.INVOICE_PAID]: 'Invoice Paid',
  [EVENT_TYPES.INVOICE_PAYMENT_FAILED]: 'Invoice Payment Failed',
  [EVENT_TYPES.INVOICE_REFUNDED]: 'Invoice Refunded',
  [EVENT_TYPES.ERRORED]: 'Error',
  [EVENT_TYPES.EVENT_PROCESSED]: 'Event Processed',
  [EVENT_TYPES.AUTOMATION_RAN]: 'Automation Ran',
  [EVENT_TYPES.AGENT_RAN]: 'Agent Ran',
  [EVENT_TYPES.AHOY_WEBHOOK_EMITTED]: 'Webhook Emitted',
  [EVENT_TYPES.AHOY_WEBHOOK_DELIVERED]: 'Webhook Delivered',
  [EVENT_TYPES.AHOY_WEBHOOK_FAILED]: 'Webhook Failed',
  [EVENT_TYPES.AHOY_WEBHOOK_REPLAY_REQUESTED]: 'Webhook Replay Requested',
  [EVENT_TYPES.AHOY_WEBHOOK_ATTEMPT_FAILED]: 'Webhook Attempt Failed',
  [EVENT_TYPES.AHOY_RECALL_BOT_COMPLETED]: 'Recall Bot Completed',
  [EVENT_TYPES.AHOY_RECALL_BOT_FAILED]: 'Recall Bot Failed',
};

// ============================================================================
// Payload Type Definitions (Discriminated Union)
// ============================================================================

/**
 * Object creation/deletion/merge event payload.
 */
export interface ObjectLifecyclePayload {
  /** Display label of the object */
  label?: string;
  /** For merge events: target object ID */
  target_object_id?: string;
  /** For merge events: source object ID */
  source_object_id?: string;
}

/**
 * Property update event payload.
 */
export interface PropertyUpdatePayload {
  /** Map of property names to new values */
  properties: Record<string, unknown>;
}

/**
 * Internal payload for server-side enrichment (e.g., old property values, defaults).
 */
export interface InternalPayload {
  /** Old property values before the update (format: {prop_name: {value, ...metadata}}) */
  old_property_values?: Record<
    string,
    { value: unknown; [key: string]: unknown }
  >;
  /** Default properties applied on object creation */
  default_properties?: Record<string, unknown>;
  /** Cascade depth for rollup calculations */
  cascade_depth?: number;
  /**
   * Point-in-time snapshots of companion JSON Schemas for json-typed properties.
   * Keyed by schema property name (e.g. "ahoy_config_schema").
   * Snapshotted at write time so historical events render with the correct schema.
   */
  companion_schemas?: Record<string, Record<string, unknown>>;
  [key: string]: unknown;
}

/**
 * Association add/remove event payload.
 */
export interface AssociationPayload {
  /** Type of association (e.g., 'ahoy_contact_to_company') */
  association_type_name: string;
  /** Entity ID of the target object */
  target_entity_id: string;
  /** Optional: Entity type name of target (e.g., 'ahoy_company') */
  target_entity_type_name?: string;
  /** Optional: Label of target object */
  target_label?: string;
}

/**
 * Email sent/received event payload.
 *
 * Mirrors the ingest payload built by the mailbox importers
 * (`apps/gmail_sync/services/gmail_message_parser.py` and the Outlook /
 * test-provider parallels). Every field is optional because historical
 * events predate individual fields; renderers must fall back gracefully.
 */
export interface EmailPayload {
  /** Email subject */
  subject?: string;
  /** Email body preview */
  body_preview?: string;
  /** Email body plain text (used for content-level dedup) */
  body_text?: string;
  /** Email body as sanitizable HTML (rendered via EmailIframe) */
  body_html?: string;
  /** Legacy body field carried by older events */
  body?: string;
  /** Short preview snippet from the provider */
  snippet?: string;
  /** Sender email address (possibly `Name <email>` form) */
  from?: string;
  /** Recipient email addresses */
  to?: string[];
  /** CC email addresses */
  cc?: string[];
  /** BCC email addresses */
  bcc?: string[];
  /** Email timestamp */
  timestamp?: string;
  /** RFC 822 Date header as recorded at ingest */
  date?: string;
  /** Email message ID */
  message_id?: string;
  /** Thread ID */
  thread_id?: string;
  /** Lowercased email address -> display name, when the provider supplied one. */
  display_names?: Record<string, string>;
  /** Provider message id used for reply threading (Gmail) */
  gmail_message_id?: string;
  /** Provider message id used for reply threading (Outlook / MS Graph) */
  outlook_message_id?: string;
  /** RFC 822 Message-ID: stable across mailbox copies (Gmail/Outlook write it) */
  internet_message_id?: string;
  /** Ingesting provider (e.g. 'gmail', 'outlook', 'harbour') */
  provider?: string;
}

/**
 * Email opened event payload.
 */
export interface EmailOpenedPayload {
  /** Email subject */
  subject?: string;
  /** Sender email address */
  from?: string;
  /** Recipient email addresses */
  to?: string[];
  /** Gmail message ID */
  gmail_message_id: string;
  /** ISO 8601 timestamp of when the email was opened */
  opened_at: string;
}

/**
 * Web form submitted event payload.
 */
export interface WebFormSubmittedPayload {
  form_name?: string;
  form_entity_id?: string;
  fields?: Array<{
    name: string;
    value: string;
    label?: string;
    type?: string;
    autocomplete?: string;
    tag?: string;
  }>;
  page_url?: string;
  page_title?: string;
}

/**
 * Web page viewed event payload.
 */
export interface WebPageViewedPayload {
  page_url?: string;
  page_title?: string;
}

/**
 * Error event payload.
 */
export interface ErrorPayload {
  /** Type of error (e.g., 'formula_error') */
  error_type?: string;
  /** Error message */
  error_message?: string;
  /** Property that caused the error (for formula errors) */
  property_type_name?: string;
}

/**
 * LinkedIn message sent/received event payload.
 */
export interface LinkedInMessagePayload {
  /** Sender entity ID (user for sent, contact for received) */
  from?: string;
  /** Recipient entity ID (contact for sent, user for received) */
  to?: string;
  /** Message content properties */
  properties?: {
    /** The message content */
    ahoy_message_content?: string;
  };
}

/**
 * Call completed/missed/voicemail event payload.
 */
export interface CallPayload {
  direction?: string;
  status?: string;
  duration_seconds?: number;
  counterparty?: string;
}

/**
 * SMS sent/received event payload.
 */
export interface SmsPayload {
  direction?: string;
  body?: string;
  sent_at?: string;
}

/**
 * Event processed event payload (automation run summary).
 * Owned by the backend emit (companion issue); optional/defensive here
 * because historical events may predate the contract.
 */
export interface EventProcessedPayload {
  /** Markdown run summary (may contain `> [!CAUTION]` blocks and entity tags). */
  summary?: string;
  /** Run outcome. */
  status?: 'success' | 'error';
}

/**
 * Automation run record payload (ahoy_automation_ran) — one automation run's
 * outcome, shared by event-triggered runs, clock fires, manual run-now, and
 * fire-time guard skips. Structured fields (status/provenance/category), never
 * text baked into the summary.
 */
export interface AutomationRanPayload {
  /** Markdown run summary (may contain `> [!CAUTION]` blocks and entity tags). */
  summary?: string;
  /** Run outcome. */
  status?: 'success' | 'error' | 'skipped';
  /** How the run arrived. */
  provenance?: 'event' | 'clock' | 'manual';
  /** Machine-readable reason slug: failure category or skip reason. */
  category?: string;
}

/**
 * A single held action within an ahoy_agent_ran run — one action the agent
 * declined to take automatically, with a stable machine reason code drawn
 * from the closed vocabulary in docs/claude/AGENT_AUTONOMY.md § The run event.
 */
export interface AgentRanHeldItem {
  /** Raw internal action id — never render. */
  action_id?: number;
  /** Stable machine reason code (e.g. 'beyond_grant'). */
  reason_code?: string;
  /** Short non-sensitive machine hint. Diagnostic only — never render. */
  detail?: string;
}

/**
 * The agent's settings snapshot at the moment of a run.
 *
 * Mirrors the `settings` block written by the approver orchestration; the
 * itemised per-scope grants (the contacts toggles and the per-pipeline
 * grant rows) are documented in docs/claude/AGENT_AUTONOMY.md.
 */
export interface AgentRanSettings {
  /** Whether party-kind creates were granted for this run. */
  add_contacts?: boolean;
  /** Whether party-kind merges were granted for this run. */
  merge_contacts?: boolean;
  /** Whether party-kind assigns were granted for this run. */
  assign_contacts?: boolean;
  /** Whether party-kind edits were granted for this run. */
  edit_contacts?: boolean;
  /**
   * Per-case-type pipeline grants. Keyed by case `EntityTypeName` (a system
   * `ahoy_*` type or a custom `org_*` type) — never a `*_timeline` projection
   * identifier or an arbitrary string; the `EntityTypeName` contract is what
   * excludes those at compile time. `Partial` because only the case types that
   * actually carry a grant row are present. Each value carries the five
   * per-case booleans written by the approver orchestration.
   */
  pipeline_grants?: Partial<
    Record<
      EntityTypeName,
      {
        mark_completed?: boolean;
        move_backward?: boolean;
        merge_duplicates?: boolean;
        assign_to_teammates?: boolean;
        edit_entered_details?: boolean;
      }
    >
  >;
}

/**
 * ahoy_agent_ran run-record payload — one agent autonomy evaluation, written
 * on EVERY run (not only approvals). Written by apps/agents/actions/resolution.py
 * (the resolve tail writes the ahoy_agent_ran run event).
 * EVERY field is optional; the renderer must render-if-present and never
 * assume a key. proposal_id / resolved_proposal_id / lane_proposal_id /
 * approved_action_ids / held[].action_id / held[].detail are raw internal
 * values — NEVER render them. reason_codes is a SORTED list of STABLE MACHINE
 * codes (never English).
 *
 * Rows stored before the settings model landed carry a `level` string instead
 * of `settings`. It is deliberately not typed here: unknown keys are ignored,
 * and every renderer must tolerate them without throwing.
 */
export interface AgentRanPayload {
  /** Autonomy policy identifier. */
  policy_id?: string;
  /** Autonomy policy version. */
  policy_version?: string | number;
  /** The agent's settings in effect for this run. */
  settings?: AgentRanSettings;
  /** Run outcome. */
  disposition?: 'approved' | 'partial' | 'held';
  /** Evaluated proposal reference. Per-user AhoyProposal — raw internal id, never render. */
  proposal_id?: string | number;
  /** Proposal id after resolution (may equal proposal_id, or a new lane proposal if split). Never render. */
  resolved_proposal_id?: string | number;
  /** Sorted list of stable machine reason codes approved actions matched. */
  reason_codes?: string[];
  /** Number of actions/records approved. Render only when typeof === 'number'. */
  approved_action_count?: number;
  /** Ids of the individual actions approved in this run. Never render. */
  approved_action_ids?: (string | number)[];
  /** Actions the agent held for manual review, with reason. */
  held?: AgentRanHeldItem[];
  /** Whether the original proposal was split to isolate the approved actions. */
  split?: boolean;
  /** New lane proposal id created by the split. Present only when split is true. Never render. */
  lane_proposal_id?: string | number;
  /** Markdown run summary — the proposer's reasoning (entity tags + alert blocks resolve). */
  summary?: string;
}

/**
 * Invoice/payment event payload (ahoy_invoice_paid / _payment_failed / _refunded).
 *
 * Mirrors the backend payload builders in
 * apps/stripe_sync/services/invoice_sync.py (build_invoice_paid_payload /
 * build_invoice_failed_payload / build_charge_refunded_payload). The payload is
 * a JSON blob, so values are serialization-native: `amount` is a DECIMAL STRING
 * in major currency units (e.g. "99.00"), parsed at the render boundary.
 * Absent values are empty strings, not undefined.
 */
export interface InvoicePaymentPayload {
  /** Decimal string in MAJOR currency units (e.g. "99.00"); '' when unknown. */
  amount?: string;
  /** ISO 4217 currency code (e.g. 'USD'); '' when unknown. */
  currency?: string;
  /** Human-facing invoice number (e.g. 'INV-0042'); absent on charge refunds. */
  invoice_number?: string;
  /** Stripe invoice id (in_...); '' when unknown. */
  stripe_invoice_id?: string;
  /** Stripe subscription id (sub_...); invoice events only. */
  stripe_subscription_id?: string;
  /** Stripe charge id (ch_...); charge-refund events only. */
  stripe_charge_id?: string;
  /** Card summary (e.g. 'visa ending 4242'); invoice events only. */
  payment_method?: string;
  /** Free-text description from Stripe. */
  description?: string;
  /** Stripe-hosted invoice page URL; invoice events only. */
  hosted_invoice_url?: string;
  /** Stripe receipt URL; charge-refund events only. */
  receipt_url?: string;
}

/**
 * Outbound webhook delivery-outcome payload
 * (ahoy_webhook_delivered / ahoy_webhook_failed / ahoy_webhook_attempt_failed).
 * Mirrors the emitter in apps/webhooks/services/outcomes.py:118-168. Every
 * field is optional: partial or historical rows must render, never crash.
 */
export interface WebhookOutcomePayload {
  /** Total delivery attempts so far. */
  attempt_count?: number;
  /** Attempt number for a single non-terminal attempt (attempt_failed only). */
  attempt?: number;
  /** HTTP status of a successful delivery (delivered only). */
  status_code?: number;
  /** HTTP status of the failing attempt (failed / attempt_failed); null when no response was received. */
  last_status?: number | null;
  /** DeliveryReason slug for the failure (e.g. 'no_secret', 'disabled', 'non_2xx'); null/absent when unknown. */
  last_error?: string | null;
  /** Truncated response body from the destination (failures); shown on expand. */
  last_response_snippet?: string | null;
  /** Wall-clock duration of the attempt, in milliseconds. */
  duration_ms?: number;
  /** Delivery id. */
  webhook_id?: string;
  /** Disambiguates multiple outcomes for one delivery. */
  outcome_suffix?: string;
}

/**
 * Discriminated union of all event payload types.
 */
export type EventPayload =
  | ObjectLifecyclePayload
  | PropertyUpdatePayload
  | AssociationPayload
  | EmailPayload
  | EmailOpenedPayload
  | LinkedInMessagePayload
  | CallPayload
  | SmsPayload
  | WebFormSubmittedPayload
  | WebPageViewedPayload
  | InvoicePaymentPayload
  | ErrorPayload
  | EventProcessedPayload
  | AutomationRanPayload
  | AgentRanPayload;

// ============================================================================
// Timeline Event (Discriminated Union)
// ============================================================================

/**
 * Base timeline event fields (common to all events).
 */
interface BaseTimelineEvent {
  event_id: string;
  entity_id: string;
  entity_type_name: string;
  seq: number;
  occurred_at: string;
  recorded_at: string;
  source_type_name?: string;
  source_id?: string;
  /**
   * Causal lineage of this event as returned by the hydration consumer:
   * ancestor event ids from root → direct parent. The LAST element is the
   * direct parent (the tree edge); earlier elements are the chain to root.
   * Absent or `[]` for a root event. Values are numeric event ids.
   */
  source_event_ids?: number[];
  metadata?: Record<string, unknown>;
  idempotency_key?: string;
  /** Server-side internal payload (old values, defaults, cascade depth) */
  internal_payload?: InternalPayload;
  /**
   * When present, indicates this event originated on an entity associated with
   * the primary entity of the current view (e.g. a Deal event shown on the
   * Contact timeline because the contact-to-deal association links them).
   * The value is the association type name. Renderers use this to switch from
   * "on this contact" to "on [associated entity badge]" presentation.
   */
  association_type_name?: string;
}

/**
 * Lifecycle event (created, deleted, merged).
 */
export interface ObjectLifecycleEvent extends BaseTimelineEvent {
  event_type_name:
    | typeof EVENT_TYPES.OBJECT_CREATED
    | typeof EVENT_TYPES.OBJECT_DELETED
    | typeof EVENT_TYPES.OBJECT_MERGED
    | typeof EVENT_TYPES.OBJECT_RESTORED;
  payload?: ObjectLifecyclePayload;
}

/**
 * Property update event.
 */
export interface PropertyUpdateEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.PROPERTY_UPDATED;
  payload?: PropertyUpdatePayload;
}

/**
 * Association added event.
 */
export interface AssociationAddedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.ASSOCIATION_ADDED;
  payload?: AssociationPayload;
}

/**
 * Association removed event.
 */
export interface AssociationRemovedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.ASSOCIATION_REMOVED;
  payload?: AssociationPayload;
}

/**
 * Email sent event.
 */
export interface EmailSentEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.EMAIL_SENT;
  payload?: EmailPayload;
}

/**
 * Email received event.
 */
export interface EmailReceivedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.EMAIL_RECEIVED;
  payload?: EmailPayload;
}

/**
 * Email opened event.
 */
export interface EmailOpenedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.EMAIL_OPENED;
  payload?: EmailOpenedPayload;
}

/**
 * LinkedIn message sent event.
 */
export interface LinkedInMessageSentEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.LINKEDIN_MESSAGE_SENT;
  payload?: LinkedInMessagePayload;
}

/**
 * LinkedIn message received event.
 */
export interface LinkedInMessageReceivedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.LINKEDIN_MESSAGE_RECEIVED;
  payload?: LinkedInMessagePayload;
}

/**
 * Call completed event.
 */
export interface CallCompletedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.AHOY_CALL_COMPLETED;
  payload?: CallPayload;
}

/**
 * Missed call event.
 */
export interface CallMissedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.AHOY_CALL_MISSED;
  payload?: CallPayload;
}

/**
 * Voicemail received event.
 */
export interface VoicemailReceivedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.AHOY_VOICEMAIL_RECEIVED;
  payload?: CallPayload;
}

/**
 * SMS sent event.
 */
export interface SmsSentEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.AHOY_SMS_SENT;
  payload?: SmsPayload;
}

/**
 * SMS received event.
 */
export interface SmsReceivedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.AHOY_SMS_RECEIVED;
  payload?: SmsPayload;
}

/**
 * Web form submitted event.
 */
export interface WebFormSubmittedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.WEB_FORM_SUBMITTED;
  payload?: WebFormSubmittedPayload;
}

/**
 * Web page viewed event.
 */
export interface WebPageViewedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.WEB_PAGE_VIEWED;
  payload?: WebPageViewedPayload;
}

/**
 * Error event (e.g., formula calculation failures).
 */
export interface ErroredEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.ERRORED;
  payload?: ErrorPayload;
}

/**
 * Event processed event (automation run summary replacing legacy audits).
 */
export interface EventProcessedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.EVENT_PROCESSED;
  payload?: EventProcessedPayload;
}

/**
 * Automation run record (ahoy_automation_ran) — the run → changes causal anchor.
 */
export interface AutomationRanEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.AUTOMATION_RAN;
  payload?: AutomationRanPayload;
}

export interface AgentRanEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.AGENT_RAN;
  payload?: AgentRanPayload;
}

export interface InvoicePaidEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.INVOICE_PAID;
  payload?: InvoicePaymentPayload;
}

export interface InvoicePaymentFailedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.INVOICE_PAYMENT_FAILED;
  payload?: InvoicePaymentPayload;
}

export interface InvoiceRefundedEvent extends BaseTimelineEvent {
  event_type_name: typeof EVENT_TYPES.INVOICE_REFUNDED;
  payload?: InvoicePaymentPayload;
}

/**
 * Discriminated union of all timeline event types.
 *
 * TypeScript can narrow the type based on event_type_name:
 *
 * @example
 * ```typescript
 * function handleEvent(event: TimelineEvent) {
 *   if (event.event_type_name === EVENT_TYPES.ASSOCIATION_ADDED) {
 *     // TypeScript knows event is AssociationAddedEvent
 *     const targetId = event.payload?.target_entity_id; // Type-safe!
 *   }
 * }
 * ```
 */
export type TimelineEvent =
  | ObjectLifecycleEvent
  | PropertyUpdateEvent
  | AssociationAddedEvent
  | AssociationRemovedEvent
  | EmailSentEvent
  | EmailReceivedEvent
  | EmailOpenedEvent
  | LinkedInMessageSentEvent
  | LinkedInMessageReceivedEvent
  | CallCompletedEvent
  | CallMissedEvent
  | VoicemailReceivedEvent
  | SmsSentEvent
  | SmsReceivedEvent
  | WebFormSubmittedEvent
  | WebPageViewedEvent
  | InvoicePaidEvent
  | InvoicePaymentFailedEvent
  | InvoiceRefundedEvent
  | ErroredEvent
  | EventProcessedEvent
  | AutomationRanEvent
  | AgentRanEvent;

// ============================================================================
// Type Guards for Runtime Validation
// ============================================================================

/**
 * Type guard for association events.
 */
export function isAssociationEvent(
  event: TimelineEvent
): event is AssociationAddedEvent | AssociationRemovedEvent {
  return (
    event.event_type_name === EVENT_TYPES.ASSOCIATION_ADDED ||
    event.event_type_name === EVENT_TYPES.ASSOCIATION_REMOVED
  );
}

/**
 * Type guard for property events.
 */
export function isPropertyEvent(
  event: TimelineEvent
): event is PropertyUpdateEvent {
  return event.event_type_name === EVENT_TYPES.PROPERTY_UPDATED;
}

/**
 * Type guard for lifecycle events.
 */
export function isLifecycleEvent(
  event: TimelineEvent
): event is ObjectLifecycleEvent {
  return (
    event.event_type_name === EVENT_TYPES.OBJECT_CREATED ||
    event.event_type_name === EVENT_TYPES.OBJECT_DELETED ||
    event.event_type_name === EVENT_TYPES.OBJECT_MERGED ||
    event.event_type_name === EVENT_TYPES.OBJECT_RESTORED
  );
}

/**
 * Type guard for web page viewed events.
 */
export function isWebPageViewedEvent(
  event: TimelineEvent
): event is WebPageViewedEvent {
  return event.event_type_name === EVENT_TYPES.WEB_PAGE_VIEWED;
}

/**
 * Type guard for email events.
 */
export function isEmailEvent(
  event: TimelineEvent
): event is EmailSentEvent | EmailReceivedEvent | EmailOpenedEvent {
  return (
    event.event_type_name === EVENT_TYPES.EMAIL_SENT ||
    event.event_type_name === EVENT_TYPES.EMAIL_RECEIVED ||
    event.event_type_name === EVENT_TYPES.EMAIL_OPENED
  );
}

/**
 * Type guard for payment events.
 */
export function isPaymentEvent(
  event: TimelineEvent
): event is
  | InvoicePaidEvent
  | InvoicePaymentFailedEvent
  | InvoiceRefundedEvent {
  return (
    event.event_type_name === EVENT_TYPES.INVOICE_PAID ||
    event.event_type_name === EVENT_TYPES.INVOICE_PAYMENT_FAILED ||
    event.event_type_name === EVENT_TYPES.INVOICE_REFUNDED
  );
}

/**
 * Validate association payload at runtime.
 */
export const isAssociationPayload = validateShape({
  association_type_name: isString,
  target_entity_id: isString,
  target_entity_type_name: optional(isString),
  target_label: optional(isString),
});

/**
 * Validate property update payload at runtime.
 */
export function isPropertyUpdatePayload(
  value: unknown
): value is PropertyUpdatePayload {
  return (
    isObject(value) &&
    hasProperty(value, 'properties') &&
    isObject(value.properties)
  );
}

// ============================================================================
// Other Types
// ============================================================================

export interface TimelineFilters {
  eventTypes: EventTypeName[];
  startDate: Date | null;
  endDate: Date | null;
  actorId: string | null;
  includeAssociated: boolean;
}

export interface TimelineConfig {
  includeAssociatedEvents: boolean;
  associatedEventTypes: (EventTypeName | string)[]; // Allow wildcard patterns like 'ahoy_deal_*'
  groupByDate: boolean;
  defaultFilters: Partial<TimelineFilters>;
}

export interface EventRendererProps {
  event: TimelineEvent;
  entityType: string;
  showDetails?: boolean;
}

export interface GroupedEvents {
  date: Date;
  dateLabel: string;
  events: TimelineEvent[];
}

// ============================================================================
// View State API Response Types
// ============================================================================

/**
 * Lightweight hydrated entity returned by the view-state API's lookup tables.
 *
 * Replaces the previous Membership / CrmObject shapes — all lookup entries
 * (users, objects, forms, audit entries) now share this uniform structure.
 * Associations are lightweight pointers resolved via lookups.entities.
 */
export interface HydratedEntity {
  entity_id: string;
  entity_type_name: string;
  ahoy_display_name: string | null;
  properties: Record<string, PropertyValue>;
  associations: Array<{
    association_type_name: string;
    target_entity_id: string;
  }>;
}

/**
 * Combined lookups from /view-state/ API response.
 *
 * All HydratedEntity data lives in the ``entities`` pool keyed by entity_id.
 * The remaining fields are lightweight pointer indexes mapping domain-specific
 * keys into the entities pool.
 */
export interface ViewStateLookups {
  /** Single pool: entity_id → HydratedEntity. */
  entities: Record<string, HydratedEntity>;
  /** user_id → entity_id | null (null = unknown/deleted user). */
  users: Record<string, string | null>;
  /** email address → entity_id. */
  emails: Record<string, string>;
  /** trigger event_id → [audit entity_ids]. */
  audits: Record<string, string[]>;
  /** form_name → entity_id. */
  forms: Record<string, string>;
  /** "entityType.propertyName" → schema metadata. */
  property_types: Record<string, PropertyTypeSchema>;
  /** "associationTypeName" → schema metadata. */
  association_types: Record<string, AssociationType>;
  /**
   * Event pool: event_id (as string key) → TimelineEvent. Holds events
   * referenced by the page but not present in it — currently each page
   * event's direct causal parent (last element of `source_event_ids`).
   * Exactly one causal layer deep: a pooled event's own ancestors are not
   * hydrated. May be partial: inaccessible or deleted parents are omitted.
   * Optional so existing lookup literals remain valid; runtime always
   * populates it (see mergeLookups / emptyViewStateLookups).
   */
  events?: Record<string, TimelineEvent>;
}

/**
 * Pagination info from /view-state/ API response.
 */
export interface ViewStatePagination {
  limit: number;
  has_more: boolean;
  next_cursor?: string;
}

/**
 * Primary entity info from /view-state/ API response.
 */
export interface PrimaryEntity {
  entity_id: string;
  entity_type_name: string;
}

/**
 * Full response from /view-state/ API endpoint.
 *
 * Returns events along with pre-built lookup tables for:
 * - Users (for displaying actor names)
 * - Objects (for displaying associated object labels)
 * - Properties (for displaying property labels and options)
 */
export interface ViewStateResponse {
  primary_entity: PrimaryEntity;
  events: TimelineEvent[];
  lookups: ViewStateLookups;
  pagination: ViewStatePagination;
  /** True when the contact's related email-thread set was capped to the most-recently-active
   *  threads (reflects only the email-thread fan-out); older related activity is not shown. */
  related_threads_truncated?: boolean;
}
